import React, { useCallback, useContext, useEffect, useState } from 'react';
import PageActionContext from '@stores/page-action-context';
import AppDataContext from '@stores/app-data-context';
import useForm, { buildInput } from '@ui/forms/use-form';
import FormHtmlInput from '@ui/forms/form-input';
import { isMinAge, isValidDate } from '@helpers/validation-helpers';
import { handleDateInputRestriction } from '@helpers/input-restrict';
import ToggleReadOnly from '@ui/toggle-readonly';

const getFullName = (firstName, middleName, lastName) => {
    return [firstName, middleName, lastName].filter((x) => x).join(' ');
};

const FormPersonalDetails = ({ data }) => {
    const [nameReadOnly, setNameReadOnly] = useState(null);

    const {
        firstName,
        middleName,
        lastName,
        mobile,
        email,
        dateOfBirth,
        residencyStatus,
    } = data;

    const pageActionCxt = useContext(PageActionContext);
    const appDataCxt = useContext(AppDataContext);

    const valueFromSession = appDataCxt.getPersonalDetails() ?? {};

    const inputs = [
        buildInput(firstName, valueFromSession.firstName),
        buildInput(middleName, valueFromSession.middleName),
        buildInput(lastName, valueFromSession.lastName),
        buildInput(mobile, valueFromSession.mobile),
        buildInput(email, valueFromSession.email),
        buildInput(dateOfBirth, valueFromSession.dateOfBirth, [
            {
                name: 'valid-date',
                message: dateOfBirth.invalidDateError,
                check: useCallback((data) => isValidDate(data.value)),
            },
            {
                name: 'min-age',
                message: dateOfBirth.minAgeYearError,
                check: useCallback((data) =>
                    isMinAge(data.value, dateOfBirth.minAgeYear)
                ),
            },
        ]),
        buildInput(residencyStatus, valueFromSession.residencyStatus),
    ];

    const handleDateKeyDown = useCallback(
        (e) => handleDateInputRestriction(e),
        []
    );

    const form = useForm({
        inputs: inputs,
        onSubmit: (values) => {
            console.log(values);
        },
    });

    useEffect(() => {
        pageActionCxt.reset();
    }, []);

    useEffect(() => {
        pageActionCxt.setNextDisabled(!form.isValid);
    }, [form.isValid]);

    const handleSubmit = (e) => {
        e?.preventDefault();

        if (pageActionCxt.isNextDisabled) return;

        if(form.dirty) {

            const fullName = getFullName(
                form.values.firstName,
                form.values.middleName,
                form.values.lastName
            );            
            appDataCxt.setPersonalDetails({ ...form.values, fullName:fullName });
        }

        var nextEventData = pageActionCxt.nextEventData;

        if (nextEventData) {
            pageActionCxt.navigate(nextEventData.url, nextEventData.isExternal);
        }
    };

    useEffect(() => {
        if (pageActionCxt.isNextClicked) {
            handleSubmit(null);
            pageActionCxt.setNextClicked(false);
        }
    }, [pageActionCxt.isNextClicked]);

    const handleToggleChange = useCallback((value) => {
        setNameReadOnly(value);
    }, []);

    const shouldFocus = useCallback(
        (name) => {
            const hasFullName = form.values.firstName && form.values.lastName;
            if (nameReadOnly === false || !hasFullName) {
                return name === 'firstName';
            }

            if (!form.values.email) return name === 'email';

            return name === 'mobile';
        },
        [nameReadOnly]
    );

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            <ToggleReadOnly
                label={data.toggleNameLabel}
                text={getFullName(
                    form.values.firstName,
                    form.values.middleName,
                    form.values.lastName
                )}
                editText={data.toggleNameEditText}
                onChange={handleToggleChange}
                readonly={form.values.firstName && form.values.lastName}
            >
                <FormHtmlInput
                    autoFocus={shouldFocus('firstName')}
                    data={firstName}
                    form={form}
                />
                <FormHtmlInput data={middleName} form={form} />
                <FormHtmlInput data={lastName} form={form} />
            </ToggleReadOnly>
            <FormHtmlInput
                data={email}
                autoFocus={shouldFocus('email')}
                form={form}
            />
            <FormHtmlInput
                data={mobile}
                autoFocus={shouldFocus('mobile')}
                form={form}
            />
            <FormHtmlInput
                data={dateOfBirth}
                onKeyDown={handleDateKeyDown}
                form={form}
            />
            <FormHtmlInput data={residencyStatus} form={form} />
        </form>
    );
};

export default FormPersonalDetails;
